import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getLateBooks,
  createLateBook,
  updateLateBook,
  deleteLateBook,
  getLateBookById,
} from '../services/lateBookService';
import Notification from '../components/Common/Notification';

// Create and export the context
export const LateBookContext = createContext();

// Context Provider component
export const LateBookProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [lateBooks, setLateBooks] = useState([]);
  const [selectedLateBook, setSelectedLateBook] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  // Function to fetch authentication headers
  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Function to show notifications
  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  // Function to close notifications
  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  // Function to fetch all late books
  const fetchAllLateBooks = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getLateBooks(authHeaders);
      setLateBooks(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching late books.';
      showNotification(errorMessage, true);
    }
  };

  // Function to fetch a late book by ID
  const fetchLateBookById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getLateBookById(id, authHeaders);
      setSelectedLateBook(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching late book with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  // Function to add a new late book
  const addLateBook = async (lateBookData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newLateBook = await createLateBook(lateBookData, authHeaders);
      setLateBooks(prevBooks => [...prevBooks, newLateBook]);
      showNotification('Late book added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding late book.';
      showNotification(errorMessage, true);
    }
  };

  // Function to edit an existing late book
  const editLateBook = async (id, lateBookData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedLateBook = await updateLateBook(id, lateBookData, authHeaders);
      setLateBooks(prevBooks =>
        prevBooks.map(book => (book.id === id ? updatedLateBook : book))
      );
      showNotification('Late book updated successfully!', false);
      return updatedLateBook;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating late book.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  // Function to remove a late book
  const removeLateBook = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteLateBook(id, authHeaders);
      setLateBooks(prevBooks => prevBooks.filter(book => book.id !== id));
      showNotification('Late book removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing late book.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllLateBooks();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <LateBookContext.Provider
      value={{
        lateBooks,
        selectedLateBook,
        fetchAllLateBooks,
        fetchLateBookById,
        addLateBook,
        editLateBook,
        removeLateBook,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </LateBookContext.Provider>
  );
};

// Custom hook to use the LateBookContext
export const useLateBook = () => useContext(LateBookContext);
